import React, { memo } from 'react';
import PropTypes from 'prop-types';
import GroupTable from 'components/UiKit/GroupTable';
import FarmRow from 'screens/Property/PropertiesLanding/components/FarmRow';
import FieldRow from 'screens/Property/PropertiesLanding/components/FieldRow';
import CropZoneRow from 'screens/Property/PropertiesLanding/components/CropZoneRow';
import CropIconMapping from 'components/CropIconMapping';

const renderCropZoneRow = (
  crop,
  farm,
  field,
  cropZone,
  geoJSONMap,
  handlers
) => {
  const cropZoneGeoJSON = geoJSONMap.get(cropZone.id);
  return (
    <GroupTable.Row
      key={cropZone.id}
      onClick={() => handlers.handleSelectedCropZone(farm, field, cropZone)}
      selectable
      selected={handlers.isCropZoneSelected(cropZone)}
    >
      <GroupTable.Cell>
        <CropZoneRow
          cropZone={cropZone}
          icon={cropZoneGeoJSON}
          fieldId={field.id}
          isEditable={false}
        />
      </GroupTable.Cell>
    </GroupTable.Row>
  );
};

const renderCropZones = (crop, farm, field, geoJSONMap, handlers) => {
  return field.cropzones.map(cropZone =>
    renderCropZoneRow(crop, farm, field, cropZone, geoJSONMap, handlers)
  );
};

const renderFieldRowGroup = (
  crop,
  properties,
  farm,
  field,
  geoJSONMap,
  handlers
) => {
  const fieldGeoJSON = geoJSONMap.get(field.id);
  return (
    <GroupTable.RowGroup
      key={field.id}
      onClick={() => handlers.handleSelectedFieldInCropGroup(crop, field)}
      selectable
      selected={handlers.isFieldSelectedInCropGroup(crop, field)}
      indeterminate={handlers.isPropertyIndeterminateInCropGroup(crop, field)}
      arrowIcon
    >
      <GroupTable.Row>
        <GroupTable.Cell>
          <FieldRow
            field={field}
            farm={farm}
            properties={properties}
            icon={fieldGeoJSON}
          />
        </GroupTable.Cell>
      </GroupTable.Row>
      {renderCropZones(crop, farm, field, geoJSONMap, handlers)}
    </GroupTable.RowGroup>
  );
};

const renderFields = (crop, properties, farm, geoJSONMap, handlers) => {
  return farm.fields.map(field =>
    renderFieldRowGroup(crop, properties, farm, field, geoJSONMap, handlers)
  );
};

const renderFarmRowGroup = (crop, farm, properties, geoJSONMap, handlers) => {
  return (
    <GroupTable.RowGroup
      key={farm.id}
      onClick={() => handlers.handleSelectedFarmInCropGroup(crop, farm)}
      selectable
      selected={handlers.isFarmSelectedInCropGroup(crop, farm)}
      indeterminate={handlers.isPropertyIndeterminateInCropGroup(crop, farm)}
      arrowIcon
    >
      <GroupTable.Row>
        <GroupTable.Cell>
          <FarmRow farm={farm} properties={properties} />
        </GroupTable.Cell>
      </GroupTable.Row>
      {renderFields(crop, properties, farm, geoJSONMap, handlers)}
    </GroupTable.RowGroup>
  );
};
// Helper function to render farms
const renderFarms = (crop, properties, geoJSONMap, handlers) => {
  return crop.farms.map(farm =>
    renderFarmRowGroup(crop, farm, properties, geoJSONMap, handlers)
  );
};

const GroupByCrop = ({
  crop,
  properties,
  handleSelectedCropGroup,
  geoJSONMap,
  isCropGroupSelected,
  handleSelectedFarmInCropGroup,
  isFarmSelectedInCropGroup,
  handleSelectedFieldInCropGroup,
  isFieldSelectedInCropGroup,
  handleSelectedCropZone,
  isCropZoneSelected,
  isPropertyIndeterminate,
  isPropertyIndeterminateInCropGroup,
  isCropGroupIndeterminate
}) => {
  const handlers = {
    handleSelectedFarmInCropGroup,
    isFarmSelectedInCropGroup,
    handleSelectedFieldInCropGroup,
    isFieldSelectedInCropGroup,
    handleSelectedCropZone,
    isCropZoneSelected,
    isPropertyIndeterminate,
    isPropertyIndeterminateInCropGroup
  };
  return (
    <GroupTable.RowGroup
      key={crop.id}
      onClick={() => handleSelectedCropGroup(crop)}
      selectable
      selected={isCropGroupSelected(crop)}
      indeterminate={isCropGroupIndeterminate(crop)}
      arrowIcon
      autoOpen={false}
    >
      <GroupTable.Row>
        <GroupTable.Cell>
          <div className="flex items-center space-x-2">
            <CropIconMapping cropObject={crop} />
            <span>{crop.name}</span>
          </div>
        </GroupTable.Cell>
      </GroupTable.Row>
      {renderFarms(crop, properties, geoJSONMap, handlers)}
    </GroupTable.RowGroup>
  );
};
GroupByCrop.propTypes = {
  crop: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    farms: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        fields: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            cropzones: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.string.isRequired
              }).isRequired
            ).isRequired
          }).isRequired
        ).isRequired
      }).isRequired
    ).isRequired
  }).isRequired,
  handleSelectedCropGroup: PropTypes.func.isRequired,
  geoJSONMap: PropTypes.instanceOf(Map).isRequired,
  isCropGroupSelected: PropTypes.func.isRequired,
  handleSelectedFarmInCropGroup: PropTypes.func.isRequired,
  isFarmSelectedInCropGroup: PropTypes.func.isRequired,
  handleSelectedFieldInCropGroup: PropTypes.func.isRequired,
  isFieldSelectedInCropGroup: PropTypes.func.isRequired,
  handleSelectedCropZone: PropTypes.func.isRequired,
  isCropZoneSelected: PropTypes.func.isRequired,
  isPropertyIndeterminate: PropTypes.func.isRequired,
  isPropertyIndeterminateInCropGroup: PropTypes.func.isRequired,
  isCropGroupIndeterminate: PropTypes.func.isRequired,
  properties: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default memo(GroupByCrop);
